import { create } from "zustand";
import { defaultFeatureFlags } from "@charter/data";
import { RemoteConfigResponseSchema } from "@charter/shared";
import { gameData } from "@/data/gameData";
import { getJson, setJson } from "@/platform/storage";
import { apiRequest } from "./api";
import { applyGateFromConfig } from "./appGate";
import { track } from "./telemetry";

const OVERRIDES_KEY = "charter.flags.overrides";

/**
 * Feature flags resolve in layers: data defaults < server remote config < local debug overrides.
 * Read flags with `useFlag` in React or `isFlagEnabled` elsewhere; never read the CSV directly.
 */
interface FlagState {
  defaults: Record<string, boolean>;
  remote: Record<string, boolean>;
  overrides: Record<string, boolean>;
  remoteLoaded: boolean;
  setOverride: (key: string, value: boolean | null) => void;
  clearOverrides: () => void;
  applyRemote: (flags: Record<string, boolean>) => void;
}

const useFlagStore = create<FlagState>((set, get) => ({
  defaults: defaultFeatureFlags(gameData),
  remote: {},
  overrides: {},
  remoteLoaded: false,
  setOverride(key, value) {
    const overrides = { ...get().overrides };
    if (value === null) delete overrides[key];
    else overrides[key] = value;
    set({ overrides });
    void setJson(OVERRIDES_KEY, overrides);
    track("flags.override", { key, value: value ?? "cleared" });
  },
  clearOverrides() {
    set({ overrides: {} });
    void setJson(OVERRIDES_KEY, {});
  },
  applyRemote(flags) {
    set({ remote: flags, remoteLoaded: true });
  },
}));

function resolveFlags(
  s: Pick<FlagState, "defaults" | "remote" | "overrides">,
): Record<string, boolean> {
  return { ...s.defaults, ...s.remote, ...s.overrides };
}

export function isFlagEnabled(key: string): boolean {
  return resolveFlags(useFlagStore.getState())[key] ?? false;
}

export function useFlag(key: string): boolean {
  return useFlagStore((s) => s.overrides[key] ?? s.remote[key] ?? s.defaults[key] ?? false);
}

/** Boot hook (main.tsx): load this device's overrides, then the server's flags (GET /v1/config). */
export async function initFeatureFlags(): Promise<void> {
  const overrides = (await getJson<Record<string, boolean>>(OVERRIDES_KEY)) ?? {};
  useFlagStore.setState({ overrides });
  try {
    const { data } = await apiRequest("/config", RemoteConfigResponseSchema);
    useFlagStore.getState().applyRemote(data.flags);
    applyGateFromConfig(data);
    if (data.dataVersion !== gameData.version) {
      track("data.version_mismatch", { client: gameData.version, server: data.dataVersion });
    }
  } catch {
    // Offline or no server: data defaults + local overrides apply.
    useFlagStore.setState({ remoteLoaded: false });
  }
}
